import { Button } from '@Components/RadixComponents/Button';

import { FlexColumn } from '@Components/common/Layouts';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import PortalTemplate from '@Components/common/Layouts/Portal';
import React, { useState } from 'react';
import DeleteConfirmationOverlay from '@Components/common/PopupOverlays/DeleteConfirmationOverlay';
import { deleteStakeholderData } from '@Services/portfolioReporting';
import { useFormContext } from 'react-hook-form';
import Icon from '@Components/common/Icon';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import IterableGovernmentStakeholder from './IterableGovernmentStakeholder';

const newOption = {
  id: `new-${uuidv4()}`,
  unit_ministry: '',
  unit_ministry_department: '',
};

function getMinistryName(interventionType: string) {
  if (interventionType === 'federalData')
    return {
      ministryName: 'Federal Ministry',
      ministryDepartmentName: 'Federal Ministry Department',
    };
  return {
    ministryName: 'Province Ministry',
    ministryDepartmentName: 'Province Ministry Department',
  };
}

function getInitialValues(
  formData: Record<string, any>,
  locationId: number,
  type: string,
  key: string,
) {
  const stakeholdersArray = formData?.stakeholder?.[key]?.find(
    (dp: Record<string, any>) => dp.id === locationId,
  )?.stakeholder;
  const selectedTypeIntitialValue = stakeholdersArray?.find(
    (stakeholder: Record<string, any>) => stakeholder?.type === type,
  )?.data;
  return selectedTypeIntitialValue;
}

function getModifiedStakeholderData(
  formValues: Record<string, any>,
  locationId: number,
  updatedValues: Record<string, any>[],
  key: string,
  formType: string,
) {
  const stakeholderData = formValues?.stakeholder?.[key]?.find(
    (location: Record<string, any>) => location.id === locationId,
  );
  const stakeholderTypeData = stakeholderData?.stakeholder?.map(
    (typeData: Record<string, any>) =>
      typeData.type === formType
        ? { ...typeData, data: updatedValues }
        : typeData,
  );
  const newLocationData = formValues?.stakeholder?.[key]?.map(
    (dps: Record<string, any>) => {
      if (dps.id === locationId)
        return { ...dps, stakeholder: stakeholderTypeData };
      return dps;
    },
  );
  return newLocationData;
}

const GovernmentStakeholder = ({
  locationId,
  dataKey,
  formType,
}: {
  locationId: number;
  dataKey: string;
  formType: string;
}) => {
  const {
    // formState: { errors },
    // register,
    setValue,
    watch,
  } = useFormContext();

  const { disabled, subDisabled } = useSelector(
    (state: any) => state.componentFormValidationSlice,
  );
  const formValues = watch();
  const initialDevelopmentPartners = getInitialValues(
    formValues,
    locationId,
    formType,
    dataKey,
  ) || [newOption];
  const [developmentPartners, setDevelopmentPartners] = useState(
    initialDevelopmentPartners,
  );
  const [selectedDP, setSelectedDP] = useState<Record<string, any> | null>(
    null,
  );
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    mutateAsync: deleteDP,
    isError,
    error,
    isLoading: isDeleting,
  } = useMutation({
    mutationFn: (id: number) => deleteStakeholderData(id),
    onError: () => {
      toast.error('Error Occured! Try again');
    },
  });

  const handleAddDevelopmentPartner = () => {
    const newPartners = [
      ...developmentPartners,
      {
        id: `new-${uuidv4()}`,
        unit_ministry: '',
        unit_ministry_department: '',
      },
    ];
    setDevelopmentPartners(newPartners);
    const newLocationData = getModifiedStakeholderData(
      formValues,
      locationId,
      newPartners,
      dataKey,
      formType,
    );
    setValue('stakeholder', {
      ...formValues?.stakeholder,
      [dataKey]: newLocationData,
    });
  };

  const handleChange = (
    selectedValue: string | number,
    selectedId: number,
    key: string,
  ) => {
    const newDevelopmentPartner = developmentPartners.map(
      (partner: Record<string, any>) => {
        return partner.id === selectedId
          ? { ...partner, [key]: selectedValue, hasChanged: true }
          : partner;
      },
    );
    setDevelopmentPartners(newDevelopmentPartner);
    const newLocationData = getModifiedStakeholderData(
      formValues,
      locationId,
      newDevelopmentPartner,
      dataKey,
      formType,
    );
    setValue('stakeholder', {
      ...formValues?.stakeholder,
      [dataKey]: newLocationData,
    });
  };

  const handleDelete = async (deleteObject: Record<string, any>) => {
    if (String(deleteObject?.id).includes('new')) {
      const newDP = developmentPartners.filter(
        (partner: Record<string, any>) => partner.id !== deleteObject?.id,
      );
      setDevelopmentPartners(newDP);
      const newLocationData = getModifiedStakeholderData(
        formValues,
        locationId,
        newDP,
        dataKey,
        formType,
      );
      setValue('stakeholder', {
        ...formValues?.stakeholder,
        [dataKey]: newLocationData,
      });
      setConfirmDelete(false);
    } else {
      await deleteDP(deleteObject.id);
      const newDP = developmentPartners.filter(
        (partner: Record<string, any>) => partner.id !== deleteObject?.id,
      );
      setDevelopmentPartners(newDP);
      const newLocationData = getModifiedStakeholderData(
        formValues,
        locationId,
        newDP,
        dataKey,
        formType,
      );
      setValue('stakeholder', {
        ...formValues?.stakeholder,
        [dataKey]: newLocationData,
      });
      setConfirmDelete(false);
    }
  };

  return (
    <>
      {confirmDelete && (
        <PortalTemplate>
          <DeleteConfirmationOverlay
            onClose={() => {
              setConfirmDelete(false);
              setSelectedDP(null);
            }}
            onDelete={() => {
              if (selectedDP) {
                handleDelete(selectedDP);
              }
            }}
            isError={isError}
            isLoading={isDeleting}
            error={error}
          />
        </PortalTemplate>
      )}
      <div className="naxatw-space-y-3">
        {developmentPartners.map((partner: Record<string, any>) => (
          <IterableGovernmentStakeholder
            key={partner.id}
            handleChange={handleChange}
            partner={partner}
            developmentPartners={developmentPartners}
            setConfirmDelete={setConfirmDelete}
            setSelectedDP={setSelectedDP}
            ministriesNames={getMinistryName(dataKey)}
            provinceId={dataKey === 'locationData' ? locationId : ''}
          />
        ))}
        <FlexColumn>
          <Button
            variant="secondary"
            onClick={e => {
              e.preventDefault();
              handleAddDevelopmentPartner();
            }}
            // checks the disabled state of the button based on the dataKey
            // if the dataKey is locationData, it checks the subDisabled based on locationId(provinceId) state
            disabled={
              dataKey === 'locationData' ? subDisabled[locationId] : disabled
            }
          >
            <Icon name="add" />
            Add More Stakeholders
          </Button>
        </FlexColumn>
      </div>
    </>
  );
};

export default GovernmentStakeholder;
